import React, { useCallback, useEffect, useState } from 'react'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { PreferenceField } from './components/preference-field'

export type PreferencesValues = {
  nudge: string
  snapDistance: string
}

type PreferencesDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  values: PreferencesValues
  onSave: (next: PreferencesValues) => void
}

const NUDGE_MIN = 1
const NUDGE_MAX = 100
const SNAP_MIN = 1
const SNAP_MAX = 24
const NUDGE_RANGE_LABEL = `Values: ${NUDGE_MIN}–${NUDGE_MAX} pt`
const SNAP_RANGE_LABEL = `Values: ${SNAP_MIN}–${SNAP_MAX} pt`

/** Whole-number string (after trim) within `[min, max]`. */
function isIntegerInRange(raw: string, min: number, max: number): boolean {
  const t = raw.trim()

  if (!/^\d+$/.test(t)) return false

  const n = parseInt(t, 10)

  return n >= min && n <= max
}

function isValidNudge(raw: string): boolean {
  return isIntegerInRange(raw, NUDGE_MIN, NUDGE_MAX)
}

function isValidSnapDistance(raw: string): boolean {
  return isIntegerInRange(raw, SNAP_MIN, SNAP_MAX)
}

/**
 * Preferences modal — Figma 9882:1076192 / error state 9882:1076206.
 */
export const PreferencesDialog: React.FC<PreferencesDialogProps> = ({
  open,
  onOpenChange,
  values,
  onSave,
}) => {
  const [nudge, setNudge] = useState(values.nudge)
  const [snapDistance, setSnapDistance] = useState(values.snapDistance)
  const [touchedNudge, setTouchedNudge] = useState(false)
  const [touchedSnap, setTouchedSnap] = useState(false)

  useEffect(() => {
    if (open) {
      setNudge(values.nudge)
      setSnapDistance(values.snapDistance)
      setTouchedNudge(false)
      setTouchedSnap(false)
    }
  }, [open, values.nudge, values.snapDistance])

  const nudgeValid = isValidNudge(nudge)
  const snapValid = isValidSnapDistance(snapDistance)
  const showNudgeError = touchedNudge && !nudgeValid
  const showSnapError = touchedSnap && !snapValid
  const canSave = nudgeValid && snapValid

  const handleCancel = useCallback(() => {
    onOpenChange(false)
  }, [onOpenChange])

  const handleSave = useCallback(() => {
    if (!isValidNudge(nudge) || !isValidSnapDistance(snapDistance)) return
    onSave({ nudge: nudge.trim(), snapDistance: snapDistance.trim() })
    onOpenChange(false)
  }, [nudge, snapDistance, onSave, onOpenChange])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="flex w-full max-w-[425px] flex-col gap-4 rounded-[10px] border border-border bg-background p-0 shadow-lg sm:max-w-[425px]"
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader className="w-full gap-[6px] space-y-0 px-6 pt-6 pr-8 text-left sm:text-left">
          <DialogTitle className="text-lg font-semibold leading-none tracking-[-0.09px] text-foreground">
            Preferences
          </DialogTitle>
          <DialogDescription className="sr-only">Preferences</DialogDescription>
        </DialogHeader>
        <DialogClose className="absolute top-4 right-4 rounded-[2px] p-0 text-[var(--base-muted-foreground,#737373)] opacity-70 transition-colors hover:bg-accent hover:text-[var(--base-foreground,#0A0A0A)]">
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogClose>

        <div className="box-border w-full px-6 pb-0 pt-0">
          <div className="w-full max-w-[377px] p-6">
            <div className="flex w-full flex-row items-start gap-6">
              <PreferenceField
                id="preferences-nudge"
                label="Nudge"
                value={nudge}
                onChange={setNudge}
                onBlur={() => setTouchedNudge(true)}
                error={showNudgeError}
                title={`Accepted range ${NUDGE_MIN}–${NUDGE_MAX} points`}
                rangeLabel={NUDGE_RANGE_LABEL}
              />
              <PreferenceField
                id="preferences-snap-distance"
                label="Snap distance"
                value={snapDistance}
                onChange={(next) => {
                  setSnapDistance(next)
                  setTouchedSnap(true)
                }}
                onBlur={() => setTouchedSnap(true)}
                error={showSnapError}
                title={`Ruler guide snap: ${SNAP_MIN}–${SNAP_MAX} pt`}
                rangeLabel={SNAP_RANGE_LABEL}
              />
            </div>
          </div>
        </div>

        <DialogFooter className="flex w-full flex-row justify-end gap-2 border-0 px-6 pb-6 pt-0 sm:justify-end">
          <Button
            variant="outline"
            type="button"
            onClick={handleCancel}
            className="h-9 rounded-md px-4 text-sm font-medium shadow-xs"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={!canSave}
            className="h-9 rounded-md px-4 text-sm font-medium shadow-xs"
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default PreferencesDialog
